// frontend/src/auth/useBootstrapSession.ts
import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

type BootstrapReady = {
  needsSelection: false;
  context: any; // you can type this later as SessionContext
  options: null;
};

type BootstrapNeedsSelection = {
  needsSelection: true;
  options: { orgs: any[]; pharmacies: any[] };
};

type BootstrapResponse = BootstrapReady | BootstrapNeedsSelection;

export type BootstrapState =
  | { status: 'idle' } // not authenticated yet
  | { status: 'loading' } // running bootstrap
  | { status: 'ready'; context: any }
  | { status: 'needsSelection'; options: BootstrapNeedsSelection['options'] }
  | { status: 'error'; message: string };

export function useBootstrapSession() {
  const { isAuthenticated, getAccessTokenSilently, isLoading } = useAuth0();
  const [state, setState] = useState<BootstrapState>({ status: 'idle' });

  // prevents double-run in React strict mode dev
  const didRun = useRef(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      didRun.current = false;
      setState({ status: 'idle' });
      return;
    }

    // avoid double bootstrap in StrictMode
    if (didRun.current) return;
    didRun.current = true;

    setState({ status: 'loading' });

    (async () => {
      try {
        const res = await fetchWithAuth(
          getAccessTokenSilently,
          'https://api-staging.mitoflux.is/api/session/bootstrap',
          { method: 'POST' }
        );

        const text = await res.text();
        if (!res.ok) {
          setState({ status: 'error', message: text || `HTTP ${res.status}` });
          return;
        }

        const data = JSON.parse(text) as BootstrapResponse;

        if (data.needsSelection) {
          setState({ status: 'needsSelection', options: data.options });
          return;
        }

        setState({ status: 'ready', context: data.context });
      } catch (e: any) {
        setState({ status: 'error', message: e?.message ?? String(e) });
      }
    })();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return state;
}
